import UserInfo from "../userInfo";
import useCalendarEvents from "./useCalendarEvents";
import useDeliveries from "./useDeliveries";
import useEquipment from "./useEquipment";
import useMapAreas from "./useMapAreas";
import useMapInfo from "./useMapInfo";
import useMapObjects from "./useMapObjects";
import useTeams from "./useTeams";
import useVendors from "./useVendors";

interface useProjectInfoProps {
    userInfo: UserInfo | null;
    projects: any[];
    overrideTimezone: string | null;
}

const useProjectInfo = (props: useProjectInfoProps) => {
    const projectInfo = (projectId: string | number | null): any | null => {
        if (!projectId) {
            return null;
        }

        return (
            props.projects.find(
                (projectInfo) => projectInfo.id === projectId || projectInfo.unique_token == projectId
            ) || null
        );
    };

    const { mapObjectsByProject, mapObjectsForProject, refreshMapObjectsByProject } = useMapObjects({
        projects: props.projects,
        projectInfo: projectInfo,
    });

    const { mapAreasByProject, mapAreasForProject, refreshMapAreasByProject } = useMapAreas({
        projects: props.projects,
        projectInfo: projectInfo,
    });

    const { equipmentByProject, equipmentForProject, refreshEquipmentByProject } = useEquipment({
        projects: props.projects,
        projectInfo: projectInfo,
    });

    const {
        mapRef,
        mapGeoJson,
        setMapGeoJson,
        mapPopup,
        mapBoxDraw,
        setMapBoxDraw,
        currentMappable,
        setCurrentMappable,
        currentMapObject,
        setCurrentMapObject,
        editCurrentMappable,
        setEditCurrentMappable,
        newMappableType,
        setNewMappableType,
        editCurrentMappablePosition,
        setEditCurrentMappablePosition,
        mapFilterDate,
        setMapFilterDate,
        currentWaypointStep,
        setCurrentWaypointStep,
        mapBounds,
        userLocation,
        setUserLocation,
        geoControlRef,
        waypointGuidance,
        setWaypointGuidance,
    } = useMapInfo({
        mapObjectsByProject: mapObjectsByProject,
        mapObjectsForProject: mapObjectsForProject,
        projectInfo: projectInfo,
    });

    const { teamsByProject, teamsForProject, refreshTeamsByProject } = useTeams({
        projects: props.projects,
        projectInfo: projectInfo,
    });

    const { vendorsByProject, vendorsForProject, refreshVendorsByProject } = useVendors({
        projects: props.projects,
        projectInfo: projectInfo,
    });

    const { createDelivery, updateDelivery, deleteDelivery } = useDeliveries({
        projects: props.projects,
        projectInfo: projectInfo,
        overrideTimezone: props.overrideTimezone,
    });

    const {
        calendarEventsFilterInfo,
        setCalendarEventsFilterInfo,
        refreshCalendarEventsByProject,
        calendarEventsByProject,
        calendarEventsForProject,
        updateCalendarEventInState,
        createCalendarEvent,
        updateCalendarEvent,
        deleteCalendarEvent,
    } = useCalendarEvents({
        userInfo: props.userInfo,
        projects: props.projects,
        projectInfo: projectInfo,
        overrideTimezone: props.overrideTimezone,
    });

    return {
        projectInfo,
        mapObjectsByProject,
        mapObjectsForProject,
        refreshMapObjectsByProject,
        mapAreasByProject,
        mapAreasForProject,
        refreshMapAreasByProject,
        equipmentByProject,
        equipmentForProject,
        refreshEquipmentByProject,
        teamsByProject,
        teamsForProject,
        refreshTeamsByProject,
        vendorsByProject,
        vendorsForProject,
        refreshVendorsByProject,
        createDelivery,
        updateDelivery,
        deleteDelivery,
        mapRef,
        mapGeoJson,
        setMapGeoJson,
        mapPopup,
        mapBoxDraw,
        setMapBoxDraw,
        currentMappable,
        setCurrentMappable,
        currentMapObject,
        setCurrentMapObject,
        editCurrentMappable,
        setEditCurrentMappable,
        newMappableType,
        setNewMappableType,
        editCurrentMappablePosition,
        setEditCurrentMappablePosition,
        mapFilterDate,
        setMapFilterDate,
        currentWaypointStep,
        setCurrentWaypointStep,
        mapBounds,
        userLocation,
        setUserLocation,
        geoControlRef,
        waypointGuidance,
        setWaypointGuidance,
        calendarEventsFilterInfo,
        setCalendarEventsFilterInfo,
        refreshCalendarEventsByProject,
        calendarEventsByProject,
        calendarEventsForProject,
        updateCalendarEventInState,
        createCalendarEvent,
        updateCalendarEvent,
        deleteCalendarEvent,
    };
};

export default useProjectInfo;
